import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import RoutingService from '../../services/routing.service';
import Session from '../../utils/session.service';
import ToolkitService from '../../services/toolkit.service';
import AngularFilterHelperService from '../../services/angular-filter-helper.service';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import { TranslatePipe } from '../../filters/translate.pipe';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { ToolkitOverviewComponent } from '../../components/toolkit/toolkit-overview/toolkit-overview.component';
import { ToolkitClientsComponent } from '../../components/toolkit/toolkit-clients/toolkit-clients.component';
import { ToolkitLibraryComponent } from '../../components/toolkit/toolkit-library/toolkit-library.component';
import { ToolkitSurveysComponent } from '../../components/toolkit/toolkit-surveys/toolkit-surveys.component';
import { ToolkitHistoryComponent } from '../../components/toolkit/toolkit-history/toolkit-history.component';
import { parseJsonLenient } from '../../utils/toolkit-json.util';

@Component({
	standalone: true,
	selector: 'toolkit',
	templateUrl: './toolkit.component.html',
	imports: [
		CommonModule,
		TranslatePipe,
		LoaderOverlayComponent,
		ToolkitOverviewComponent,
		ToolkitClientsComponent,
		ToolkitLibraryComponent,
		ToolkitSurveysComponent,
		ToolkitHistoryComponent
	]
})
export default class ToolkitComponent implements OnInit {
	private routingService: RoutingService = inject(RoutingService);
	private session: Session = inject(Session);
	private toolkitService: ToolkitService = inject(ToolkitService);
	private angularFilterHelperService: AngularFilterHelperService = inject(AngularFilterHelperService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);

	vm: any = undefined;

	constructor() {
		this.vm = this;
	}

	ngOnInit() {
		this.routingService.validateAccess('toolkit').then(() => {
			const user = this.session.getSession()?.user || {};
			const allowedSubcategories = ['PSYCHOLOGY', 'GENERAL_MEDICAL_PRACTICE', 'PHYSIOTHERAPY'];
			if (!(user.businessUserType === 'SERVICE_PROVIDER' && !!user.service && allowedSubcategories.includes(user.service.subcategory))) {
				this.angularRoutingHelperService.goHome(user);
				return;
			}
			this.onResolve();
		});
	}

	onResolve() {
		this.vm.user = this.session.getSession()?.user;
		this.vm.loading = true;
		this.vm.activeTab = 'overview';

		// Deep-link support (used by client drill-down "Assign" button).
		// We also allow a one-shot localStorage fallback so navigation from other routes can preselect a client.
		const qpTab = (this.angularRoutingHelperService.getQueryParam('tab') || '').toString();
		const qpAssignClientId = (this.angularRoutingHelperService.getQueryParam('assignClientId') || '').toString();
		const lsAssignClientId = (() => {
			try {
				return (localStorage.getItem('toolkitAssignClientId') || '').toString();
			} catch {
				return '';
			}
		})();
		if (qpTab) this.vm.activeTab = qpTab;
		this.vm.assignClientId = qpAssignClientId || lsAssignClientId || '';

		this.vm.setActiveTab = this.setActiveTab;
		this.vm.applyClientFilters = this.applyClientFilters;
		this.vm.resetClientFilters = this.resetClientFilters;
		this.vm.initials = this.initials;
		this.vm.openClient = this.openClient;
		this.vm.closeClient = this.closeClient;
		this.vm.previewItem = this.previewItem;
		this.vm.assignItem = this.assignItem;
		this.vm.applyLibraryFilters = this.applyLibraryFilters;
		this.vm.resetHistoryFilters = this.resetHistoryFilters;
		this.vm.themeName = this.themeName;
		this.vm.themeIcon = this.themeIcon;
		this.vm.resourceFormatLabel = this.resourceFormatLabel;
		this.vm.assessmentLabel = this.assessmentLabel.bind(this);

		this.vm.clientSearch = '';
		this.vm.clientTheme = 'ALL';
		this.vm.clientStatus = 'ALL';
		this.vm.clientSort = 'next_session';
		this.vm.clientToolkitFilter = 'ALL';
		this.vm.clientSurveyFilter = 'ALL';
		this.vm.clients = [];
		this.vm.filteredClients = [];
		this.vm.selectedClient = null;
		this.vm.clientDetail = null;
		this.vm.clientDetailLoading = false;
		this.vm.catalogue = null;
		this.vm.libraryItems = [];
		this.vm.filteredLibraryItems = [];
		this.vm.librarySearch = '';
		this.vm.surveyItems = [];
		this.vm.activity = [];
		// History filters. These are stored on vm so tab switches keep state.
		this.vm.historySearch = this.vm.historySearch || '';
		this.vm.historyFrom = this.vm.historyFrom || '';
		this.vm.historyTo = this.vm.historyTo || '';
		this.vm.historyItemId = this.vm.historyItemId || 'ALL';
		this.vm.historyKind = this.vm.historyKind || 'ALL';
		this.vm.historyRows = [];
		this.vm._historyAssignments = [];
		this.vm._historyAssessmentAssignments = [];
		this.vm.rebuildHistoryRows = this.rebuildHistoryRows;
		this.vm.rebuildClientThemes = this.rebuildClientThemes.bind(this);
		this.vm.getClientSummary = this.getClientSummary.bind(this);
		this.vm.openClientRoute = this.openClientRoute.bind(this);
		this.vm.startAssignForClient = this.startAssignForClient.bind(this);
		this.vm.startPromptAssign = this.startPromptAssign.bind(this);
		this.vm.openPromptResult = this.openPromptResult.bind(this);
		this.vm.openHistoryRow = this.openHistoryRow.bind(this);
		this.vm.getPromptStateLabel = this.getPromptStateLabel.bind(this);
		this.vm.getPromptSummary = this.getPromptSummary.bind(this);
		this.vm.getEffectivePrompt = this.getEffectivePrompt.bind(this);
		this.vm.getPlanCompletedCount = this.getPlanCompletedCount.bind(this);
		this.vm.getPlanProgressLabel = this.getPlanProgressLabel.bind(this);
		this.vm.getClientSessionLabel = this.getClientSessionLabel.bind(this);

		this.toolkitService.getCatalogue(
			{},
			(catalogue) => {
				this.vm.catalogue = catalogue;
				// ToolkitCatalogue schema from ToolkitApiResource:
				// { themes: ToolkitTheme[], resources: ToolkitResource[], assessments: ToolkitAssessment[], aiRules: ToolkitAiRule[] }
				this.vm.themes = Array.isArray(catalogue?.themes) ? catalogue.themes : [];
				this.vm.resources = Array.isArray(catalogue?.resources) ? catalogue.resources : [];
				this.vm.assessments = Array.isArray(catalogue?.assessments) ? catalogue.assessments : [];

				// Library tab = toolkit resources. Surveys tab = assessments.
				this.vm.libraryItems = this.vm.resources;
				this.vm.surveyItems = this.vm.assessments;

				// Init library filtering state.
				this.vm.libraryThemeFilter = this.vm.libraryThemeFilter || 'all';
				this.vm.libraryFormatFilter = this.vm.libraryFormatFilter || 'all';
				this.vm.librarySearch = this.vm.librarySearch || '';
				this.applyLibraryFilters();
				this.rebuildClientThemes();
				this.applyClientFilters();
				this.rebuildHistoryRows();
				this.vm.loading = false;
			},
			() => {
				this.vm.loading = false;
			}
		);

		this.toolkitService.getUserCustomers({}, (clients) => {
			this.vm.clients = clients || [];
			console.log(clients);
			// Backend now returns nextSessionAt derived from bookings.
			(this.vm.clients || []).forEach((c) => {
				c.nextSessionText = this.formatNextSession(c?.nextSessionAt);
			});
			this.rebuildClientThemes();
			this.applyClientFilters();
			this.rebuildHistoryRows();
		});

		// History is derived from therapist assignments (resources only for now).
		this.toolkitService.getAssignments({}, (items) => {
			this.vm._historyAssignments = Array.isArray(items) ? items : [];
			this.rebuildClientThemes();
			this.applyClientFilters();
			this.rebuildHistoryRows();
		});

		// Survey assignments for summary + history.
		this.toolkitService.getAssessmentAssignments({}, (items) => {
			this.vm._historyAssessmentAssignments = Array.isArray(items) ? items : [];
			this.rebuildClientThemes();
			this.applyClientFilters();
			this.rebuildHistoryRows();
		});
	}

	setActiveTab(tab: string) {
		this.vm.activeTab = tab;
	}

		private formatNextSession(nextSessionAt: any): string {
			if (!nextSessionAt) return '-';
			const d = new Date(nextSessionAt);
			if (!isFinite(d.getTime())) return '-';
		// Use the user's configured language (not the workstation language).
		const rawLocale =
			(this.vm?.user?.deviceLanguage || this.vm?.user?.language || '').toString().trim() || undefined;
		const locale = rawLocale ? rawLocale.replace('_', '-') : undefined;

		// Keep format compact and locale-friendly (e.g. "26 May, 10:00").
		const day = d.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
		const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
		return `${day}, ${time}`;
	}

	applyClientFilters() {
		let list: any[] = Array.isArray(this.vm.clients) ? this.vm.clients : [];
		const q: string = (this.vm.clientSearch || '').toString().trim().toLowerCase();

		// Theme filter: derived from assigned toolkit resources/assessments (themeId).
		const themeId: string = (this.vm.clientTheme || 'ALL').toString();
		if (themeId !== 'ALL') {
			list = list.filter((c) => {
				const ids: any[] = Array.isArray(c?._themeIds) ? c._themeIds : [];
				return ids.some((id) => (id ?? '').toString() === themeId);
			});
		}

		// Status filter (best-effort).
		const status: string = (this.vm.clientStatus || 'ALL').toString();
		if (status !== 'ALL') {
			list = list.filter((c) => {
				const summary = this.getClientSummary(c);
				const key = (summary?.statusKey || '').toString().toUpperCase();
				if (status === 'ACTIVE') return key === 'ACTIVE';
				if (status === 'OVERDUE') return key === 'OVERDUE';
				if (status === 'DONE') return key === 'DONE';
				if (status === 'NEW') return key === 'NEW';
				const s = (c?.status || c?.toolkitStatus || '').toString().toUpperCase();
				return s === status.toUpperCase();
			});
		}

		const toolkitFilter: string = (this.vm.clientToolkitFilter || 'ALL').toString();
		if (toolkitFilter !== 'ALL') {
			list = list.filter((c) => {
				const summary = this.getClientSummary(c);
				const total = Number(summary?.toolsTotal || 0);
				const done = Number(summary?.toolsCompleted || 0);
				const pending = total - done;
				if (toolkitFilter === 'PENDING') return pending > 0;
				if (toolkitFilter === 'COMPLETED') return done > 0;
				if (toolkitFilter === 'NONE') return total === 0;
				return true;
			});
		}

		const surveyFilter: string = (this.vm.clientSurveyFilter || 'ALL').toString();
		if (surveyFilter !== 'ALL') {
			list = list.filter((c) => {
				const summary = this.getClientSummary(c);
				const total = Number(summary?.surveysTotal || 0);
				const done = Number(summary?.surveysCompleted || 0);
				const pending = total - done;
				if (surveyFilter === 'PENDING') return pending > 0;
				if (surveyFilter === 'COMPLETED') return done > 0;
				if (surveyFilter === 'NONE') return total === 0;
				return true;
			});
		}

		if (q) {
			list = list.filter((c) => {
				const fullName = `${c?.firstname || ''} ${c?.lastname || ''}`.toLowerCase();
				const email = (c?.email || '').toLowerCase();
				const code = (c?.personalCode || '').toLowerCase();
				return fullName.includes(q) || email.includes(q) || code.includes(q);
			});
		}

		// Sorting (best-effort).
		const sort: string = (this.vm.clientSort || 'next_session').toString();
		list = list.slice().sort((a, b) => {
			const ap = (a?.assessmentPrompt?.state || '').toString().toLowerCase();
			const bp = (b?.assessmentPrompt?.state || '').toString().toLowerCase();
			if (ap === 'due' && bp !== 'due') return -1;
			if (bp === 'due' && ap !== 'due') return 1;

			if (sort === 'name') {
				const an = (`${a?.firstname || ''} ${a?.lastname || ''}`).trim().toLowerCase();
				const bn = (`${b?.firstname || ''} ${b?.lastname || ''}`).trim().toLowerCase();
				return an.localeCompare(bn);
			}

			// Default: next session (soonest first). Push missing nextSessionAt to the bottom.
			const at = a?.nextSessionAt ? new Date(a.nextSessionAt).getTime() : Number.POSITIVE_INFINITY;
			const bt = b?.nextSessionAt ? new Date(b.nextSessionAt).getTime() : Number.POSITIVE_INFINITY;
			if (at !== bt) return at - bt;

			// Tie-breaker: name.
			const an = (`${a?.firstname || ''} ${a?.lastname || ''}`).trim().toLowerCase();
			const bn = (`${b?.firstname || ''} ${b?.lastname || ''}`).trim().toLowerCase();
			return an.localeCompare(bn);
		});

		this.vm.filteredClients = list;
		// Overview tab should only show clients who have at least one assigned exercise or survey.
		// Other tabs (Clients) keep showing the full filtered list.
		this.vm.filteredClientsOverview = list.filter((c) => {
			const s = this.getClientSummary(c);
			const tools = Number(s?.toolsTotal || 0);
			const surveys = Number(s?.surveysTotal || 0);
			return tools + surveys > 0;
		});
	}

	resetClientFilters() {
		this.vm.clientTheme = 'ALL';
		this.vm.clientStatus = 'ALL';
		this.vm.clientSort = 'next_session';
		this.vm.clientToolkitFilter = 'ALL';
		this.vm.clientSurveyFilter = 'ALL';
		this.vm.clientSearch = '';
		this.applyClientFilters();
	}

	initials(c: any) {
		const f = (c?.firstname || '?').toString().trim();
		const l = (c?.lastname || '').toString().trim();
		return `${f ? f[0] : '?'}${l ? l[0] : ''}`.toUpperCase();
	}

	openClient(c: any) {
		// Keep existing Clients tab behavior (inline drill-down),
		// but Overview uses a dedicated route-based client details view.
		if (!c) return;
		this.vm.selectedClient = c;
		this.vm.clientDetail = null;
		this.vm.clientDetailLoading = true;
		const id = c.id || c.clientId || c.userId;
		if (!id) {
			this.vm.clientDetailLoading = false;
			return;
		}
		this.toolkitService.getClientDetail(
			{ id },
			(detail) => {
				this.vm.clientDetail = detail;
				this.vm.clientDetailLoading = false;
			},
			() => {
				this.vm.clientDetailLoading = false;
			}
		);
	}

	closeClient() {
		this.vm.selectedClient = null;
		this.vm.clientDetail = null;
		this.vm.clientDetailLoading = false;
	}

	openClientRoute(c: any) {
		if (!c) return;
		const id = c.id || c.clientId || c.userId;
		if (!id) return;
		this.angularRoutingHelperService.setPath(['/toolkit/client', id], false, false);
	}

	startAssignForClient(c: any) {
		if (!c) return;
		const id = c.id || c.clientId || c.userId;
		if (!id) return;
		try {
			localStorage.setItem('toolkitAssignClientId', id.toString());
		} catch {}
		this.vm.assignClientId = id.toString();
		this.vm.activeTab = 'library';
		this.angularRoutingHelperService.setPath('/toolkit', false, false, { tab: 'library', assignClientId: id });
	}

	startPromptAssign(c: any) {
		const id = c?.id || c?.clientId || c?.userId;
		const prompt = c?.assessmentPrompt || null;
		const assessmentId = prompt?.assessmentId || 'eek2';
		if (!id) return;
		const rawSessionIndex = Number(prompt?.sessionIndex ?? c?.nextBooking?.sessionIndex ?? c?.nextSessionIndex ?? 0);
		const normalizedSessionIndex = rawSessionIndex === 5 ? 4 : rawSessionIndex === 9 ? 8 : rawSessionIndex;
		const derivedTimepoint =
			prompt?.timepoint
			|| (normalizedSessionIndex === 4 ? 'T2' : normalizedSessionIndex === 8 ? 'T3' : null);
		const query: any = {
			clientId: id,
			timepoint: derivedTimepoint,
			bookingId: prompt?.bookingId || c?.nextBooking?.bookingId || c?.nextBooking?.id || null,
			source: 'prompt'
		};
		this.angularRoutingHelperService.setPath(['/toolkit/assessment/assign', assessmentId], false, false, query);
	}

	openPromptResult(c: any) {
		const id = c?.id || c?.clientId || c?.userId;
		const prompt = this.getEffectivePrompt(c);
		const assignmentId = prompt?.assignmentId || null;
		const assessmentId = prompt?.assessmentId || 'eek2';
		if (!id || !assignmentId) return;
		this.angularRoutingHelperService.setPath(
			['/toolkit/assessment/preview', assessmentId],
			false,
			false,
			{ preview: 1, client_id: id, assignment_id: assignmentId, return_to: '/toolkit' }
		);
	}

	openHistoryRow(row: any) {
		if (!row) return;
		const status = (row?.status || '').toString().toLowerCase();
		if (status !== 'completed') return;

		const raw = row?.raw || null;
		const assignmentId = raw?.id != null ? raw.id.toString() : '';
		const clientId = raw?.clientUserId != null ? raw.clientUserId.toString() : '';
		if (!assignmentId || !clientId) return;

		if (row?.type === 'survey') {
			const assessmentId = raw?.assessmentId != null ? raw.assessmentId.toString() : '';
			if (!assessmentId) return;
			this.angularRoutingHelperService.setPath(
				['/toolkit/assessment/preview', assessmentId],
				false,
				false,
				{ preview: 1, client_id: clientId, assignment_id: assignmentId, return_to: '/toolkit' }
			);
			return;
		}

		const resourceId = raw?.resourceId != null ? raw.resourceId.toString() : '';
		if (!resourceId) return;
		this.angularRoutingHelperService.setPath(
			['/toolkit/preview', 'resource', resourceId],
			false,
			false,
			{ preview: 1, client_id: clientId, assignment_id: assignmentId, return_to: '/toolkit' }
		);
	}

	previewItem(item: any) {
		if (!item) return;
		const isAssessment = !!(item?.questionsJson || item?.scoringJson || item?.assessmentId) && !item?.format;
		const id = item?.id || item?.resourceId || item?.assessmentId;
		if (!id) return;
		if (isAssessment) {
			this.angularRoutingHelperService.setPath(['/toolkit/assessment/preview', id], false, false);
		} else {
			this.angularRoutingHelperService.setPath(['/toolkit/preview', 'resource', id], false, false);
		}
	}

	assignItem(item: any) {
		if (!item) return;
		const isAssessment = !!(item?.questionsJson || item?.scoringJson || item?.assessmentId) && !item?.format;
		const id = item?.id || item?.resourceId || item?.assessmentId;
		if (!id) return;
		const clientId = (this.vm?.assignClientId || '').toString().trim();
		const q = clientId ? { clientId } : undefined;
		if (clientId) {
			try {
				localStorage.removeItem('toolkitAssignClientId');
			} catch {}
			this.vm.assignClientId = '';
		}
		if (isAssessment) {
			this.angularRoutingHelperService.setPath(['/toolkit/assessment/assign', id], false, false, q);
		} else {
			this.angularRoutingHelperService.setPath(['/toolkit/assign', 'resource', id], false, false, q);
		}
	}

	applyLibraryFilters() {
		const list: any[] = Array.isArray(this.vm.libraryItems) ? this.vm.libraryItems : [];
		const theme: string = (this.vm.libraryThemeFilter || 'all').toString();
		const format: string = (this.vm.libraryFormatFilter || 'all').toString();
		const q: string = (this.vm.librarySearch || '').toString().trim().toLowerCase();

		let out = list;
		if (theme !== 'all') {
			out = out.filter((it) => (it?.theme || it?.themeId || '').toString() === theme);
		}
		if (format !== 'all') {
			out = out.filter((it) => (it?.format || it?.type || '').toString() === format);
		}
		if (q) {
			out = out.filter((it) => {
				const title = (it?.title || it?.name || '').toString().toLowerCase();
				const summary = (it?.summary || it?.description || '').toString().toLowerCase();
				const tags = (it?.tags || []).toString().toLowerCase();
				return title.includes(q) || summary.includes(q) || tags.includes(q);
			});
		}

		this.vm.filteredLibraryItems = out;
	}

	resetHistoryFilters() {
		this.vm.historySearch = '';
		this.vm.historyClientId = 'ALL';
		this.vm.historyType = 'ALL';
		this.vm.historyStatus = 'ALL';
		this.vm.historyItemId = 'ALL';
		this.vm.historyFrom = '';
		this.vm.historyTo = '';
	}

	rebuildHistoryRows() {
		const list: any[] = Array.isArray(this.vm._historyAssignments) ? this.vm._historyAssignments : [];
		const surveys: any[] = Array.isArray(this.vm._historyAssessmentAssignments) ? this.vm._historyAssessmentAssignments : [];
		const clients: any[] = Array.isArray(this.vm.clients) ? this.vm.clients : [];
		const resources: any[] = Array.isArray(this.vm.resources) ? this.vm.resources : [];
		const assessments: any[] = Array.isArray(this.vm.assessments) ? this.vm.assessments : [];

		const rows = list.map((a) => {
			const client = clients.find((c) => c?.id === a?.clientUserId) || null;
			const res = resources.find((r) => r?.id === a?.resourceId) || null;
			const themeId = res?.themeId || res?.theme_id || res?.theme || null;
			return {
				kind: 'toolkit',
				dateTime: a?.assignedAt || a?.created || null,
				date: (a?.assignedAt || a?.created || '').toString().slice(0, 10),
				client: client ? `${client.firstname || ''} ${client.lastname || ''}`.trim() : a?.clientUserId || '',
				item: res ? res.title || res.name || res.id : a?.resourceId || '',
				theme: themeId ? this.themeName(themeId) : '',
				type: 'toolkit',
				status: this.deriveResourceStatus(a),
				resultPrimary: this.deriveResourceResultPrimary(a),
				resultSecondary: this.deriveResourceResultSecondary(a),
				raw: a
			};
		});

		const surveyRows = surveys.map((a) => {
			const client = clients.find((c) => c?.id === a?.clientUserId) || null;
			const instr = assessments.find((x) => x?.id === a?.assessmentId) || null;
			const themeId = instr?.themeId || instr?.theme_id || instr?.theme || null;
			return {
				kind: 'survey',
				dateTime: a?.assignedAt || a?.created || null,
				date: (a?.assignedAt || a?.created || '').toString().slice(0, 10),
				client: client ? `${client.firstname || ''} ${client.lastname || ''}`.trim() : a?.clientUserId || '',
				item: this.assessmentLabel(instr) || a?.assessmentId || '',
				theme: themeId ? this.themeName(themeId) : (instr?.description || instr?.type || ''),
				type: 'survey',
				status: this.deriveAssessmentStatus(a),
				resultPrimary: this.deriveAssessmentResultPrimary(a),
				resultSecondary: this.deriveAssessmentResultSecondary(a),
				raw: a
			};
		});

		const all = rows.concat(surveyRows).sort((a, b) => {
			const at = a?.dateTime ? new Date(a.dateTime).getTime() : 0;
			const bt = b?.dateTime ? new Date(b.dateTime).getTime() : 0;
			return bt - at;
		});

		this.vm.historyRows = all;
	}

	private isOverdue(dueAt: any): boolean {
		if (!dueAt) return false;
		const d = new Date(dueAt);
		if (!isFinite(d.getTime())) return false;
		const today = new Date();
		const t = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
		const due = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
		return due < t;
	}

	private deriveResourceStatus(a: any): string {
		const completions = Array.isArray(a?.completions) ? a.completions : [];
		if (completions.length > 0) return 'completed';
		if (this.isOverdue(a?.dueAt)) return 'overdue';
		return 'pending';
	}

	private deriveResourceResultPrimary(a: any): string {
		const completions = Array.isArray(a?.completions) ? a.completions : [];
		if (completions.length > 0) return `${completions.length}`;
		return '';
	}

	private deriveResourceResultSecondary(a: any): string {
		if (a?.dueAt) return `${(a.dueAt || '').toString().slice(0, 10)}`;
		return '';
	}

	private deriveAssessmentStatus(a: any): string {
		const status = (a?.status || '').toString().toLowerCase();
		if (status === 'completed' || a?.completedAt) return 'completed';
		if (this.isOverdue(a?.dueAt)) return 'overdue';
		return 'pending';
	}

	private deriveAssessmentResultPrimary(a: any): string {
		if (a?.score != null) {
			const band = a?.scoreBand ? ` (${a.scoreBand})` : '';
			return `${a.score}${band}`;
		}
		return '';
	}

	private deriveAssessmentResultSecondary(a: any): string {
		if (a?.dueAt) return `${(a.dueAt || '').toString().slice(0, 10)}`;
		return '';
	}

	assessmentLabel(assessment: any): string {
		if (!assessment) return '';

		const directCode = (assessment?.code || assessment?.englishAbbreviation || assessment?.engAbbreviation || '').toString().trim();
		if (directCode) return directCode;

		const parsedQuestions = parseJsonLenient(assessment?.questionsJson || assessment?.questions_json || null);
		const parsedCode = (
			parsedQuestions?.code ||
			parsedQuestions?.instrument?.code ||
			parsedQuestions?.abbreviation ||
			parsedQuestions?.englishAbbreviation ||
			''
		).toString().trim();
		if (parsedCode) return parsedCode;

		return (assessment?.title || assessment?.name || assessment?.id || '').toString().trim();
	}

	getClientSummary(client: any): any {
		const id = client?.id;
		const assignments: any[] = Array.isArray(this.vm._historyAssignments) ? this.vm._historyAssignments : [];
		const surveys: any[] = Array.isArray(this.vm._historyAssessmentAssignments) ? this.vm._historyAssessmentAssignments : [];
		const effectivePrompt = this.getEffectivePrompt(client);

		const myTools = assignments.filter((a) => a?.clientUserId === id && !(a?.isDeleted));
		const mySurveys = surveys.filter((a) => a?.clientUserId === id && !(a?.isDeleted));

		const toolsCompleted = myTools.filter((a) => Array.isArray(a?.completions) && a.completions.length > 0).length;
		const surveysCompleted = mySurveys.filter((a) => (a?.status || '').toString().toLowerCase() === 'completed' || a?.completedAt).length;

		const toolsOverdue = myTools.filter((a) => this.isOverdue(a?.dueAt) && !(Array.isArray(a?.completions) && a.completions.length > 0)).length;
		const surveysOverdue = mySurveys.filter((a) => this.isOverdue(a?.dueAt) && !((a?.status || '').toString().toLowerCase() === 'completed' || a?.completedAt)).length;

		const overdueCount = toolsOverdue + surveysOverdue;
		const promptState = (effectivePrompt?.state || '').toString().toLowerCase();
		const rawPromptSessionIndex = Number(effectivePrompt?.sessionIndex ?? client?.nextBooking?.sessionIndex ?? client?.nextSessionIndex ?? 0);
		const promptSessionIndex = rawPromptSessionIndex === 5
			? 4 : rawPromptSessionIndex === 9 ? 8 : rawPromptSessionIndex;
		const highlightReassessment = promptState === 'due' && isFinite(promptSessionIndex) && promptSessionIndex >= 4;

		let statusKey = 'ACTIVE';
		if (myTools.length + mySurveys.length === 0) statusKey = 'NEW';
		else if (overdueCount > 0) statusKey = 'OVERDUE';
		else if (toolsCompleted === myTools.length && surveysCompleted === mySurveys.length) statusKey = 'DONE';
		if (promptState === 'due') statusKey = 'ACTIVE';

		return {
			toolsTotal: myTools.length,
			toolsCompleted,
			surveysTotal: mySurveys.length,
			surveysCompleted,
			overdueCount,
			statusKey,
			promptState: effectivePrompt?.state || null,
			promptSessionIndex: isFinite(promptSessionIndex) ? promptSessionIndex : null,
			highlightReassessment
		};
	}

	getEffectivePrompt(client: any): any | null {
		const prompt = client?.assessmentPrompt || null;
		if (!prompt) return null;

		const assignment = this.findCurrentPromptAssignment(client);
		if (!assignment) return prompt;

		const effective = {
			...prompt,
			assignmentId: assignment?.id ?? prompt?.assignmentId ?? null,
			timepoint: assignment?.timepoint || prompt?.timepoint || this.promptTimepoint(client, prompt),
			sessionIndex: assignment?.sessionIndex ?? prompt?.sessionIndex ?? client?.nextBooking?.sessionIndex ?? client?.nextSessionIndex ?? null,
			assignedAt: assignment?.assignedAt || assignment?.created || prompt?.assignedAt || null,
			sentAt: assignment?.assignedAt || assignment?.created || prompt?.sentAt || prompt?.assignedAt || null,
			dueAt: assignment?.dueAt || prompt?.dueAt || null,
			score: assignment?.score ?? prompt?.score ?? null,
			completedAt: assignment?.completedAt || prompt?.completedAt || null
		};

		if (assignment?.completedAt || (assignment?.status || '').toString().toLowerCase() === 'completed') {
			effective.state = 'completed';
			return effective;
		}
		if (this.isOverdue(assignment?.dueAt)) {
			effective.state = 'overdue';
			return effective;
		}
		effective.state = 'sent';
		return effective;
	}

	getPromptStateLabel(prompt: any): string {
		const state = (prompt?.state || '').toString().toLowerCase();
		if (state === 'due') return this.angularFilterHelperService.translate('Toolkit.Prep.Status.Due');
		if (state === 'sent') return this.angularFilterHelperService.translate('Toolkit.Prep.Status.Sent');
		if (state === 'overdue') return this.angularFilterHelperService.translate('Toolkit.Prep.Status.Overdue');
		if (state === 'completed') return this.angularFilterHelperService.translate('Toolkit.Prep.Status.Completed');
		return '';
	}

	getPromptSummary(prompt: any): string {
		if (!prompt) return '';
		const sessionIndex = prompt?.sessionIndex != null
			? this.angularFilterHelperService.stringBuilder(
				this.angularFilterHelperService.translate('Toolkit.Clients.Session.Short'),
				prompt.sessionIndex
			)
			: this.angularFilterHelperService.translate('Toolkit.Clients.Session.Generic');
		const state = (prompt?.state || '').toString().toLowerCase();
		if (state === 'due') {
			const baselineDate = prompt?.baselineAt ? this.formatShortDate(prompt.baselineAt) : '';
			if (baselineDate && prompt?.baselineScore != null) {
				return this.angularFilterHelperService.stringBuilder(
					this.angularFilterHelperService.translate('Toolkit.Clients.Prompt.Body.WithBaseline'),
					baselineDate,
					prompt.baselineScore,
					sessionIndex
				);
			}
			if (prompt?.baselineScore != null) {
				return this.angularFilterHelperService.stringBuilder(
					this.angularFilterHelperService.translate('Toolkit.Clients.Prompt.Body.WithScore'),
					prompt.baselineScore,
					sessionIndex
				);
			}
			return this.angularFilterHelperService.stringBuilder(
				this.angularFilterHelperService.translate('Toolkit.Clients.Prompt.Body.Default'),
				sessionIndex
			);
		}
		if (state === 'sent') {
			const sentAt = prompt?.sentAt || prompt?.assignedAt || prompt?.createdAt || prompt?.updatedAt || null;
			const sent = sentAt ? this.formatShortDate(sentAt) : '';
			const due = prompt?.dueAt ? this.formatShortDate(prompt.dueAt) : '';
			if (sent && due) {
				return this.angularFilterHelperService.stringBuilder(
					this.angularFilterHelperService.translate('Toolkit.Prep.Summary.Sent.WithSentAndDue'),
					sent,
					due
				);
			}
			if (sent) {
				return this.angularFilterHelperService.stringBuilder(
					this.angularFilterHelperService.translate('Toolkit.Prep.Summary.Sent.WithSent'),
					sent
				);
			}
			if (due) {
				return this.angularFilterHelperService.stringBuilder(
					this.angularFilterHelperService.translate('Toolkit.Prep.Summary.Sent.WithDue'),
					due
				);
			}
			return this.angularFilterHelperService.translate('Toolkit.Prep.Summary.Sent.Default');
		}
		if (state === 'overdue') {
			return prompt?.dueAt
				? this.angularFilterHelperService.stringBuilder(
					this.angularFilterHelperService.translate('Toolkit.Prep.Summary.Overdue.WithDue'),
					this.formatShortDate(prompt.dueAt)
				)
				: this.angularFilterHelperService.translate('Toolkit.Prep.Summary.Overdue.Default');
		}
		if (state === 'completed') {
			const delta = prompt?.deltaFromBaseline;
			if (delta == null) {
				return this.angularFilterHelperService.stringBuilder(
					this.angularFilterHelperService.translate('Toolkit.Prep.Summary.Completed.WithoutDelta'),
					prompt?.score ?? '-'
				);
			}
			const deltaText = delta < 0 ? '▼' + Math.abs(delta) : '▲' + delta;
			return this.angularFilterHelperService.stringBuilder(
				this.angularFilterHelperService.translate('Toolkit.Prep.Summary.Completed.WithDelta'),
				prompt?.score ?? '-',
				deltaText
			);
		}
		return '';
	}

	getPlanProgressLabel(client: any): string {
		const plan = client?.planProgress || null;
		if (!plan) {
			const summary = this.getClientSummary(client);
			return `${summary?.surveysCompleted || 0} / ${summary?.surveysTotal || 0}`;
		}
		return `${this.getPlanCompletedCount(client)} / ${plan.total || 3}`;
	}

	getPlanCompletedCount(client: any): number {
		const plan = client?.planProgress || null;
		if (!plan) return 0;

		const total = Math.max(0, Number(plan?.total || 0));
		const prompt = this.getEffectivePrompt(client);
		const promptState = (prompt?.state || '').toString().toLowerCase();
		const rawCompleted = Number(plan?.completed || 0);
		let completed = isFinite(rawCompleted) ? Math.max(0, rawCompleted) : 0;

		const dueIndex = this.planIndexFromTimepoint(plan?.dueTimepoint);
		if (promptState === 'due' && dueIndex >= 0) {
			completed = Math.max(completed, dueIndex);
		}

		const promptIndex = this.planIndexFromTimepoint(
			prompt?.timepoint || this.sessionIndexToTimepoint(prompt?.sessionIndex)
		);
		if (['sent', 'overdue', 'completed'].includes(promptState) && promptIndex >= 0) {
			completed = Math.max(completed, promptIndex + 1);
		}

		return Math.max(0, Math.min(total, completed));
	}

	getClientSessionLabel(client: any): string {
		const idx = client?.bookedThroughSessionIndex ?? client?.assessmentPrompt?.sessionIndex ?? client?.nextSessionIndex;
		const startsAt = client?.nextSessionAt || client?.nextBooking?.startsAt || null;
		if (!startsAt) return this.angularFilterHelperService.translate('Toolkit.Clients.NoSessionBooked');
		const dateLabel = this.formatSessionDateTime(startsAt);
		if (idx != null && dateLabel) {
			return this.angularFilterHelperService.stringBuilder(
				this.angularFilterHelperService.translate('Toolkit.Clients.Session.Full'),
				idx,
				dateLabel
			);
		}
		if (idx != null) {
			return this.angularFilterHelperService.stringBuilder(
				this.angularFilterHelperService.translate('Toolkit.Clients.Session.Short'),
				idx
			);
		}
		return dateLabel || this.angularFilterHelperService.translate('Toolkit.Clients.NoSessionBooked');
	}

	private formatShortDate(value: any): string {
		if (!value) return '';
		const d = new Date(value);
		if (!isFinite(d.getTime())) return '';
		const rawLocale =
			(this.vm?.user?.deviceLanguage || this.vm?.user?.language || '').toString().trim() || undefined;
		const locale = rawLocale ? rawLocale.replace('_', '-') : undefined;
		return d.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
	}

	private formatSessionDateTime(value: any): string {
		if (!value) return '';
		const d = new Date(value);
		if (!isFinite(d.getTime())) return '';
		const rawLocale =
			(this.vm?.user?.deviceLanguage || this.vm?.user?.language || '').toString().trim() || undefined;
		const locale = rawLocale ? rawLocale.replace('_', '-') : undefined;
		const datePart = d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
		const timePart = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false });
		return `${datePart}, ${timePart}`;
	}

	private planIndexFromTimepoint(timepoint: any): number {
		const key = (timepoint || '').toString().trim().toUpperCase();
		if (key === 'T1_BASELINE') return 0;
		if (key === 'T2') return 1;
		if (key === 'T3') return 2;
		return -1;
	}

	private sessionIndexToTimepoint(sessionIndex: any): string | null {
		const numeric = Number(sessionIndex || 0);
		if (!isFinite(numeric)) return null;
		if (numeric === 1) return 'T1_BASELINE';
		if (numeric === 4 || numeric === 5) return 'T2';
		if (numeric === 8 || numeric === 9) return 'T3';
		return null;
	}

	private promptTimepoint(client: any, prompt?: any): string | null {
		return (
			prompt?.timepoint
			|| client?.planProgress?.dueTimepoint
			|| this.sessionIndexToTimepoint(prompt?.sessionIndex ?? client?.nextBooking?.sessionIndex ?? client?.nextSessionIndex)
		);
	}

	private findCurrentPromptAssignment(client: any): any | null {
		const clientId = (client?.id ?? client?.clientId ?? client?.userId ?? '').toString();
		const prompt = client?.assessmentPrompt || null;
		if (!clientId || !prompt) return null;

		const targetAssessmentId = (prompt?.assessmentId || 'eek2').toString();
		const targetTimepoint = this.promptTimepoint(client, prompt);
		const surveys: any[] = Array.isArray(this.vm._historyAssessmentAssignments) ? this.vm._historyAssessmentAssignments : [];

		const matches = surveys
			.filter((a) => {
				if (!a || a?.isDeleted) return false;
				if ((a?.clientUserId ?? a?.clientId ?? a?.userId ?? '').toString() !== clientId) return false;
				if ((a?.assessmentId || '').toString() !== targetAssessmentId) return false;
				if (targetTimepoint) {
					const assignmentTimepoint = (a?.timepoint || this.sessionIndexToTimepoint(a?.sessionIndex) || '').toString().toUpperCase();
					if (assignmentTimepoint !== targetTimepoint.toString().toUpperCase()) return false;
				}
				return true;
			})
			.sort((a, b) => {
				const at = new Date(a?.completedAt || a?.assignedAt || a?.created || a?.updatedAt || 0).getTime();
				const bt = new Date(b?.completedAt || b?.assignedAt || b?.created || b?.updatedAt || 0).getTime();
				return bt - at;
			});

		return matches[0] || null;
	}

	// Derive client themes from assignments + catalogue resources/assessments.
	// This keeps the UI filters in sync with the DB-backed themeId on toolkit_resource/toolkit_assessment.
	rebuildClientThemes() {
		const clients: any[] = Array.isArray(this.vm.clients) ? this.vm.clients : [];
		const resources: any[] = Array.isArray(this.vm.resources) ? this.vm.resources : [];
		const assessments: any[] = Array.isArray(this.vm.assessments) ? this.vm.assessments : [];
		const assignments: any[] = Array.isArray(this.vm._historyAssignments) ? this.vm._historyAssignments : [];
		const surveyAssignments: any[] = Array.isArray(this.vm._historyAssessmentAssignments)
			? this.vm._historyAssessmentAssignments
			: [];

		const resourceById = new Map<string, any>();
		resources.forEach((r) => {
			const id = (r?.id ?? r?.resourceId ?? '').toString();
			if (id) resourceById.set(id, r);
		});
		const assessmentById = new Map<string, any>();
		assessments.forEach((a) => {
			const id = (a?.id ?? a?.assessmentId ?? '').toString();
			if (id) assessmentById.set(id, a);
		});

		const themesByClient = new Map<string, Set<string>>();
		const add = (clientId: any, themeId: any) => {
			const cid = (clientId ?? '').toString();
			const tid = (themeId ?? '').toString();
			if (!cid || !tid) return;
			if (!themesByClient.has(cid)) themesByClient.set(cid, new Set<string>());
			themesByClient.get(cid)!.add(tid);
		};

		assignments.forEach((a) => {
			if (a?.isDeleted) return;
			const cid = a?.clientUserId ?? a?.clientId ?? a?.customerId ?? a?.userId;
			const rid = (a?.resourceId ?? a?.id ?? '').toString();
			if (!cid || !rid) return;
			const r = resourceById.get(rid);
			const tid = r?.themeId ?? r?.theme_id ?? r?.theme;
			add(cid, tid);
		});

		surveyAssignments.forEach((a) => {
			if (a?.isDeleted) return;
			const cid = a?.clientUserId ?? a?.clientId ?? a?.customerId ?? a?.userId;
			const aid = (a?.assessmentId ?? a?.id ?? '').toString();
			if (!cid || !aid) return;
			const asmt = assessmentById.get(aid);
			const tid = asmt?.themeId ?? asmt?.theme_id ?? asmt?.theme;
			add(cid, tid);
		});

		clients.forEach((c) => {
			const cid = (c?.id ?? c?.clientId ?? c?.userId ?? '').toString();
			const ids = cid && themesByClient.has(cid) ? Array.from(themesByClient.get(cid)!) : [];
			ids.sort((a, b) => a.localeCompare(b));

			// Used by filters.
			c._themeIds = ids;

			// Used by UI for display (translation keys where possible).
			c.themeKeys = ids.map((id) => this.themeName(id)).filter((x) => !!x);
		});
	}

	themeName(themeId: string) {
		const themes: any[] = Array.isArray(this.vm.themes) ? this.vm.themes : [];
		const found = themes.find((t) => (t?.id || '').toString() === (themeId || '').toString());

		const rawId = (found?.id ?? themeId ?? '').toString();
		const rawName = (found?.name ?? '').toString();

		const keyFromId = this.themeTranslationKey(rawId);
		if (keyFromId) return keyFromId;
		const keyFromName = this.themeTranslationKey(rawName);
		if (keyFromName) return keyFromName;

		return rawName || rawId || '';
	}

	private themeTranslationKey(input: string): string | null {
		const s = (input || '')
			.toString()
			.trim()
			.toLowerCase()
			.replace(/&/g, 'and')
			.replace(/[^\w\s]/g, ' ')
			.replace(/[_-]/g, ' ')
			.replace(/\s+/g, ' ');

		if (!s) return null;
		if (s === 'anxiety') return 'Toolkit.Theme.Anxiety';
		if (s === 'stress' || s === 'stress & overload' || s === 'stress and overload' || s === 'overload') return 'Toolkit.Theme.Stress';
		if (s === 'low mood' || s === 'lowmood') return 'Toolkit.Theme.LowMood';
		if (s === 'sleep') return 'Toolkit.Theme.Sleep';
		if (s === 'self esteem' || s === 'selfesteem' || s === 'self esteem') return 'Toolkit.Theme.SelfEsteem';
		if (s === 'self-esteem') return 'Toolkit.Theme.SelfEsteem';
		if (s === 'habits') return 'Toolkit.Theme.Habits';
		if (s === 'motivation') return 'Toolkit.Theme.Motivation';
		if (s === 'emotional regulation') return 'Toolkit.Theme.EmotionalRegulation';
		if (s === 'focus and concentration' || s == 'focus concentration') return 'Toolkit.Theme.FocusConcentration';
		if (s === 'relationships') return 'Toolkit.Theme.Relationships';
		return null;
	}

	themeIcon(themeId: string) {
		const themes: any[] = Array.isArray(this.vm.themes) ? this.vm.themes : [];
		const found = themes.find((t) => (t?.id || '').toString() === (themeId || '').toString());
		return found?.icon || '';
	}

	resourceFormatLabel(format: string) {
		const f = (format || '').toString();
		if (!f) return '';
		if (f === 'psychoeducation') return 'Toolkit.Format.Psychoeducation';
		if (f === 'exercise') return 'Toolkit.Format.Exercise';
		if (f === 'worksheet') return 'Toolkit.Format.Worksheet';
		if (f === 'check-in') return 'Toolkit.Format.CheckIn';
		if (f === 'planner') return 'Toolkit.Format.Planner';
		return f;
	}
}
